<div class="min-h-screen bg-gradient-to-br from-mist/30 via-white to-sand/20">
  <div class="max-w-6xl mx-auto px-6 py-8">
    <!-- Messages -->
    @if (errorMessage()) {
      <div class="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-3">
        <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span class="font-medium">{{ errorMessage() }}</span>
      </div>
    }

    @if (successMessage()) {
      <div class="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-3">
        <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span class="font-medium">{{ successMessage() }}</span>
      </div>
    }

    @if (isLoading()) {
      <div class="flex items-center justify-center py-20">
        <div class="text-center">
          <div class="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
          <p class="text-charcoal/60 mt-4">Loading blog...</p>
        </div>
      </div>
    }

    <!-- Editor Form -->
    @if (!isLoading()) {
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <!-- Main Content - 3 columns -->
        <div class="lg:col-span-3 space-y-4">
          <!-- Title Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-2">
              Title <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              [(ngModel)]="blog.title"
              (ngModelChange)="onTitleChange()"
              placeholder="Enter an engaging blog title..."
              class="w-full px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-lg font-semibold text-charcoal placeholder-charcoal/40"
            />
          </div>

          <!-- Excerpt Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-2">
              Excerpt <span class="text-red-500">*</span>
              <span class="text-xs text-charcoal/50 font-normal ml-2">Brief summary for listing pages</span>
            </label>
            <textarea
              [(ngModel)]="blog.excerpt"
              placeholder="Write a compelling excerpt..."
              rows="3"
              class="w-full px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40 resize-none"
            ></textarea>
          </div>

          <!-- Content Editor Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-3">
              Content <span class="text-red-500">*</span>
            </label>
            <div
              class="border-2 border-charcoal/20 rounded-lg overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all cursor-text"
              (click)="focusEditor()"
            >
              <div #editor class="min-h-[400px] cursor-text"></div>
            </div>
          </div>

          <!-- SEO Settings Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <div class="flex items-center gap-2 mb-4">
              <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <h3 class="text-lg font-semibold text-charcoal">SEO Settings</h3>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-semibold text-charcoal mb-2">Meta Title</label>
                <input
                  type="text"
                  [(ngModel)]="blog.meta_title"
                  placeholder="SEO optimized title"
                  maxlength="60"
                  class="w-full px-4 py-2.5 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40"
                />
                <p class="text-xs text-charcoal/50 mt-1">{{ blog.meta_title.length }}/60 characters</p>
              </div>

              <div>
                <label class="block text-sm font-semibold text-charcoal mb-2">Meta Description</label>
                <textarea
                  [(ngModel)]="blog.meta_description"
                  placeholder="SEO meta description"
                  rows="2"
                  maxlength="160"
                  class="w-full px-4 py-2.5 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40 resize-none"
                ></textarea>
                <p class="text-xs text-charcoal/50 mt-1">{{ blog.meta_description.length }}/160 characters</p>
              </div>

              <div>
                <label class="block text-sm font-semibold text-charcoal mb-2">Meta Keywords</label>
                <input
                  type="text"
                  [(ngModel)]="blog.meta_keywords"
                  placeholder="keyword1, keyword2, keyword3"
                  class="w-full px-4 py-2.5 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar - 1 column -->
        <div class="space-y-4">
          <!-- Featured Image Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-3">
              Featured Image <span class="text-red-500">*</span>
            </label>

            @if (featuredImagePreview || existingFeaturedImage) {
              <div class="relative mb-3 rounded-lg overflow-hidden group">
                <img
                  [src]="featuredImagePreview || getImageUrl(existingFeaturedImage)"
                  alt="Featured image preview"
                  class="w-full aspect-video object-cover"
                />
                <button
                  type="button"
                  (click)="featuredImagePreview = null; existingFeaturedImage = null; blog.featured_image = null"
                  class="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            }

            <input
              type="file"
              id="featured_image"
              accept="image/*"
              (change)="onFeaturedImageSelect($event)"
              class="hidden"
            />
            <label
              for="featured_image"
              class="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg text-charcoal font-semibold cursor-pointer hover:border-primary hover:text-primary transition-all"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              Choose Image
            </label>
            <p class="text-xs text-charcoal/50 mt-2">Max 5MB • Recommended 1200x630px</p>
          </div>

          <!-- Tags Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-3">Tags</label>

            <div class="flex flex-wrap gap-2 mb-3 min-h-[40px]">
              @for (tag of blog.tags; track tag) {
                <span class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-mist rounded-lg text-sm font-medium">
                  {{ tag }}
                  <button type="button" (click)="removeTag(tag)" class="hover:bg-white/20 rounded p-0.5 transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </span>
              }
            </div>

            <div class="flex gap-2">
              <input
                type="text"
                [(ngModel)]="tagInput"
                (keydown)="onTagInputKeydown($event)"
                placeholder="Type and press Enter"
                class="flex-1 px-4 py-2.5 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40"
              />
              <button
                type="button"
                (click)="addTag()"
                class="px-4 py-2.5 bg-primary text-mist font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Add
              </button>
            </div>
          </div>

          <!-- Publishing Options Card -->
          <div class="bg-white rounded-xl shadow-md border border-charcoal/10 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-3">Publishing Options</label>

            <label class="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                [(ngModel)]="blog.is_featured"
                class="w-5 h-5 rounded border-2 border-charcoal/30 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
              />
              <div>
                <span class="text-charcoal font-medium group-hover:text-primary transition-colors">Featured Post</span>
                <p class="text-xs text-charcoal/50">Show in featured carousel</p>
              </div>
            </label>
          </div>

          <!-- URL Preview Card -->
          <div class="bg-gradient-to-br from-primary/5 to-sand/10 rounded-xl border border-primary/20 p-6">
            <label class="block text-sm font-semibold text-charcoal mb-2">URL Preview</label>
            <p class="text-sm text-charcoal/60 break-all">
              /blogs/<span class="font-mono text-primary">{{ blog.slug || 'blog-post-slug' }}</span>
            </p>
          </div>
        </div>
      </div>
    }
  </div>
</div>

<!-- Image Cropper Modal -->
<app-image-cropper-modal
  #imageCropper
  [imageFile]="selectedImageForCrop"
  [aspectRatio]="16 / 9"
  [resizeToWidth]="1200"
  [resizeToHeight]="630"
  (cropped)="onImageCropped($event)"
  (cancelled)="onImageCropCancelled()"
></app-image-cropper-modal>
